import { version } from "../../package.json";
import { store } from "../store";
import { LightElement } from "../ui";
import { renderAppShellView } from "./app-shell-view";

type Route = { page: "library" | "import" | "join" | "player" | "settings" | "help"; id?: string };

function route(): Route {
  const [page, id] = location.hash.replace(/^#\/?/, "").split("/");
  if (page === "import" || page === "settings" || page === "join" || page === "help") return { page };
  if (page === "player" && id) return { page, id };
  return { page: "library" };
}

export class AppShell extends LightElement {
  connectedCallback(): void {
    this.render();
    this.listen(window, "hashchange", () => this.renderRoute());
    const unsubscribe = store.subscribe((state) => this.applySettings(state.settings));
    this.abort.signal.addEventListener("abort", unsubscribe);
    void store.load();
  }

  private render(): void {
    this.innerHTML = renderAppShellView(version);
    this.renderRoute();
    this.setupInstall();
  }

  private renderRoute(): void {
    const main = this.querySelector("main");
    if (!main) return;
    const current = route();
    if (current.page === "import") main.innerHTML = "<script-import-flow></script-import-flow>";
    else if (current.page === "settings") main.innerHTML = "<app-settings-page></app-settings-page>";
    else if (current.page === "help") main.innerHTML = "<app-help></app-help>";
    else if (current.page === "join") main.innerHTML = "<script-player join-only></script-player>";
    else if (current.page === "player") main.innerHTML = `<script-player script-id="${current.id}"></script-player>`;
    else main.innerHTML = "<script-library></script-library>";
    const currentHref = current.page === "library" ? "#/" : current.page === "settings" ? "#/settings" : current.page === "help" ? "#/help" : current.page === "join" ? "#/join" : current.page === "import" ? "#/import" : "";
    this.querySelectorAll<HTMLAnchorElement>(".app-header nav a").forEach((link) => {
      if (link.getAttribute("href") === currentHref) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    });
    window.scrollTo({ top: 0 });
  }

  private applySettings(settings: import("../types").AppSettings): void {
    const root = document.documentElement;
    root.dataset.theme = settings.theme;
    root.classList.toggle("a11y-large-text", settings.largerText);
    root.classList.toggle("a11y-open-dyslexic", settings.openDyslexic);
    root.classList.toggle("a11y-high-contrast", settings.highContrast);
    root.classList.toggle("a11y-reduce-motion", settings.reduceMotion);
  }

  private setupInstall(): void {
    const button = this.querySelector<HTMLButtonElement>("[data-install]");
    if (!button) return;
    let prompt: Event & { prompt?: () => Promise<void> } | undefined;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Keep the manual install entry point visible because iOS Safari does not
    // emit beforeinstallprompt, even when the app can be added to the home screen.
    button.hidden = false;
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault(); prompt = event; button.hidden = false;
    }, { signal: this.abort.signal });
    this.listen(button, "click", () => {
      if (prompt?.prompt) {
        void prompt.prompt();
        button.hidden = true;
        return;
      }
      alert(ios
        ? "To install LineByLine, tap Safari’s Share button, then choose ‘Add to Home Screen’."
        : "To install LineByLine, open your browser menu and choose ‘Install app’ or ‘Add to Home screen’."
      );
    });
  }
}

customElements.define("app-shell", AppShell);
